import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { canAccess, roleHome } from './constants/roleAccess';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './config/queryClient';
import { Toaster } from 'react-hot-toast';
import { useThemeStore } from './store/themeStore';
import { MainLayout } from './components/layout/MainLayout';
import { useAuthStore } from './store/authStore';
import logoMark from './assets/logo-mark.png';

// ── Eager-loaded (tiny, needed on first render) ────────────────────────────
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { RegisterExpediteur } from './pages/RegisterExpediteur';
import { LoginExpediteur } from './pages/LoginExpediteur';
import { PublicTracking } from './pages/PublicTracking';
import { TenantSelector } from './pages/TenantSelector';
import { FeedbackPage } from './pages/FeedbackPage';

// ── Lazy-loaded pages ──────────────────────────────────────────────────────
const LivreurLinkPage = lazy(() => import('./pages/LivreurLinkPage').then(m => ({ default: m.LivreurLinkPage })));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const CreateColis = lazy(() => import('./pages/CreateColis').then(m => ({ default: m.CreateColis })));
const ColisList = lazy(() => import('./pages/ColisList').then(m => ({ default: m.ColisList })));
const ColisDetails = lazy(() => import('./pages/ColisDetails').then(m => ({ default: m.ColisDetails })));
const ImportColis = lazy(() => import('./pages/ImportColis').then(m => ({ default: m.ImportColis })));
const ExpeditionsList = lazy(() => import('./pages/ExpeditionsList').then(m => ({ default: m.ExpeditionsList })));
const CreateExpedition = lazy(() => import('./pages/CreateExpedition').then(m => ({ default: m.CreateExpedition })));
const ExpeditionDetails = lazy(() => import('./pages/ExpeditionDetails').then(m => ({ default: m.ExpeditionDetails })));
const LivreursList = lazy(() => import('./pages/LivreursList').then(m => ({ default: m.LivreursList })));
const LivreurDetails = lazy(() => import('./pages/LivreurDetails').then(m => ({ default: m.LivreurDetails })));
const LivreurApp = lazy(() => import('./pages/LivreurApp').then(m => ({ default: m.LivreurApp })));
const ParticulierApp = lazy(() => import('./pages/ParticulierApp').then(m => ({ default: m.ParticulierApp })));
const EcommerceOrderForm = lazy(() => import('./pages/EcommerceOrderForm').then(m => ({ default: m.EcommerceOrderForm })));
const Merchants = lazy(() => import('./pages/Merchants').then(m => ({ default: m.Merchants })));
const Reconciliation = lazy(() => import('./pages/Reconciliation').then(m => ({ default: m.Reconciliation })));
const VendeurPortal = lazy(() => import('./pages/VendeurPortal').then(m => ({ default: m.VendeurPortal })));
const Integrations = lazy(() => import('./pages/Integrations').then(m => ({ default: m.Integrations })));
const NationalTransportForm = lazy(() => import('./pages/NationalTransportForm').then(m => ({ default: m.NationalTransportForm })));
const CargoInternationalForm = lazy(() => import('./pages/CargoInternationalForm').then(m => ({ default: m.CargoInternationalForm })));
const FacturesList = lazy(() => import('./pages/FacturesList').then(m => ({ default: m.FacturesList })));
const FactureDetails = lazy(() => import('./pages/FactureDetails').then(m => ({ default: m.FactureDetails })));
const CreateFacture = lazy(() => import('./pages/CreateFacture').then(m => ({ default: m.CreateFacture })));
const EditFacture = lazy(() => import('./pages/EditFacture').then(m => ({ default: m.EditFacture })));
const Notifications = lazy(() => import('./pages/Notifications').then(m => ({ default: m.Notifications })));
const NotificationTemplates = lazy(() => import('./pages/NotificationTemplates').then(m => ({ default: m.NotificationTemplates })));
const Wallet = lazy(() => import('./pages/Wallet').then(m => ({ default: m.Wallet })));
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const Reports = lazy(() => import('./pages/Reports').then(m => ({ default: m.Reports })));
const UserProfile = lazy(() => import('./pages/UserProfile').then(m => ({ default: m.UserProfile })));
const SuperAdminConfig = lazy(() => import('./pages/SuperAdminConfig').then(m => ({ default: m.SuperAdminConfig })));
const QuotaManagement = lazy(() => import('./pages/QuotaManagement').then(m => ({ default: m.QuotaManagement })));
const SystemMonitor = lazy(() => import('./pages/SystemMonitor').then(m => ({ default: m.SystemMonitor })));
const ClientPortal = lazy(() => import('./pages/client/ClientPortal').then(m => ({ default: m.ClientPortal })));
const ClientDashboard = lazy(() => import('./pages/client/ClientDashboard').then(m => ({ default: m.ClientDashboard })));
const LivreurWallet = lazy(() => import('./pages/LivreurWallet').then(m => ({ default: m.LivreurWallet })));
const QARegression = lazy(() => import('./pages/QARegression').then(m => ({ default: m.QARegression })));
const ReseauVueEnsemble = lazy(() => import('./pages/reseau/ReseauVueEnsemble').then(m => ({ default: m.ReseauVueEnsemble })));
const Sites = lazy(() => import('./pages/reseau/Sites').then(m => ({ default: m.Sites })));
const Manifestes = lazy(() => import('./pages/reseau/Manifestes').then(m => ({ default: m.Manifestes })));
const Tournees = lazy(() => import('./pages/reseau/Tournees').then(m => ({ default: m.Tournees })));
const Compensation = lazy(() => import('./pages/reseau/Compensation').then(m => ({ default: m.Compensation })));
const DossiersBoard = lazy(() => import('./pages/transit/DossiersBoard').then(m => ({ default: m.DossiersBoard })));
const DossierDetail = lazy(() => import('./pages/transit/DossierDetail').then(m => ({ default: m.DossierDetail })));

// ── Suspense fallback ──────────────────────────────────────────────────────
const PageLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0f0a1a]">
    <div className="flex flex-col items-center gap-4">
      <div className="relative w-48 h-48 flex items-center justify-center">
        <div className="absolute inset-0 border-8 border-white/10 border-t-violet-500 rounded-full animate-spin" />
        <img src={logoMark} alt="CeDo Trace" className="w-28 h-28 object-contain" />
      </div>
      <span className="text-sm text-white/50">Chargement…</span>
    </div>
  </div>
);

const ThemeApplier: React.FC = () => {
  const { theme } = useThemeStore();
  // Extraire les primitives de l'objet couleurs_theme pour éviter
  // que la référence objet change à chaque update du store et re-déclenche l'effect
  const primaire = useAuthStore(s => s.user?.tenant?.couleurs_theme?.primaire);
  const secondaire = useAuthStore(s => s.user?.tenant?.couleurs_theme?.secondaire);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);

    const primary = theme === 'dark' ? (primaire || '#8B5CF6') : '#8B5CF6';
    const secondary = theme === 'dark' ? (secondaire || '#EC4899') : '#EC4899';

    root.style.setProperty('--color-primary', primary);
    root.style.setProperty('--color-secondary', secondary);
  }, [theme, primaire, secondaire]);

  return null;
};

/** Pages that super_admin can access WITHOUT selecting a tenant */
const SUPER_ADMIN_FREE_PATHS = [
  '/dashboard',
  '/select-tenant',
  '/profile',
  '/super-admin',
];

/**
 * ProtectedRoute — wraps authenticated pages.
 * If the user is super_admin and hasn't selected a tenant yet,
 * redirect to /select-tenant (except for free paths).
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode; requireTenant?: boolean }> = ({
  children,
  requireTenant = true,
}) => {
  const { isAuthenticated, user, activeTenantId } = useAuthStore();
  const location = useLocation();

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  // Un rôle ne voit que ses pages (livreur → son app, vendeur → son portail,
  // agent → opérations…) ; le reste renvoie vers son accueil. Le super admin
  // et l'admin d'entreprise ne sont jamais restreints.
  if (user && !canAccess(user.role, location.pathname)) {
    return <Navigate to={roleHome(user.role)} replace />;
  }

  // Super admin must select tenant before accessing operational pages
  if (
    requireTenant &&
    user?.role === 'super_admin' &&
    !activeTenantId
  ) {
    return <Navigate to="/select-tenant" replace />;
  }

  return <MainLayout>{children}</MainLayout>;
};

/**
 * SuperAdminOnly — wraps super-admin pages (no tenant required).
 */
const SuperAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (user?.role !== 'super_admin') return <Navigate to="/dashboard" replace />;
  return <MainLayout>{children}</MainLayout>;
};

/** Guard for the TenantSelector page */
const TenantSelectorGuard: React.FC = () => {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (user?.role !== 'super_admin') return <Navigate to="/dashboard" replace />;
  return <TenantSelector />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeApplier />
      <BrowserRouter>
        <Toaster position="top-right" />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes */}
            <Route path="/track" element={<PublicTracking />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/register-expediteur" element={<RegisterExpediteur />} />
            {/* Ancien chemin — conservé pour les liens/QR déjà diffusés */}
            <Route path="/register-particulier" element={<RegisterExpediteur />} />
            <Route path="/login-telephone" element={<LoginExpediteur />} />
            <Route path="/feedback/:token" element={<FeedbackPage />} />
            <Route path="/livreur/:token" element={<LivreurLinkPage />} />

            {/* Super admin tenant picker (no MainLayout wrapping) */}
            <Route path="/select-tenant" element={<TenantSelectorGuard />} />

            {/* Dashboard — accessible to all authenticated users */}
            <Route path="/dashboard" element={<ProtectedRoute requireTenant={false}><Dashboard /></ProtectedRoute>} />

            {/* Public client portal routes */}
            <Route path="/client" element={<ClientPortal />} />
            <Route path="/client/dashboard" element={<ClientDashboard />} />

            {/* Livreur app — no main layout, accessible to LIVREUR role */}
            <Route path="/livreur-app" element={<ProtectedRoute requireTenant={false}><LivreurApp /></ProtectedRoute>} />
            <Route path="/mes-envois" element={<ProtectedRoute requireTenant={false}><ParticulierApp /></ProtectedRoute>} />
            <Route path="/national-transport" element={<ProtectedRoute><NationalTransportForm /></ProtectedRoute>} />
            <Route path="/cargo-international" element={<ProtectedRoute><CargoInternationalForm /></ProtectedRoute>} />
            {/* Module E-Commerce & Suivi de Livraison */}
            <Route path="/ecommerce/new" element={<ProtectedRoute><EcommerceOrderForm /></ProtectedRoute>} />
            <Route path="/merchants" element={<ProtectedRoute><Merchants /></ProtectedRoute>} />
            <Route path="/reconciliation" element={<ProtectedRoute><Reconciliation /></ProtectedRoute>} />
            <Route path="/integrations" element={<ProtectedRoute><Integrations /></ProtectedRoute>} />
            <Route path="/vendeur" element={<ProtectedRoute requireTenant={false}><VendeurPortal /></ProtectedRoute>} />

            {/* Operational routes — require tenant context for super_admin */}
            <Route path="/colis" element={<ProtectedRoute><ColisList /></ProtectedRoute>} />
            <Route path="/colis/import" element={<ProtectedRoute><ImportColis /></ProtectedRoute>} />
            <Route path="/expeditions" element={<ProtectedRoute><ExpeditionsList /></ProtectedRoute>} />
            <Route path="/expeditions/new" element={<ProtectedRoute><CreateExpedition /></ProtectedRoute>} />
            <Route path="/expeditions/:id" element={<ProtectedRoute><ExpeditionDetails /></ProtectedRoute>} />
            <Route path="/reseau" element={<ProtectedRoute><ReseauVueEnsemble /></ProtectedRoute>} />
            <Route path="/reseau/sites" element={<ProtectedRoute><Sites /></ProtectedRoute>} />
            <Route path="/reseau/manifestes" element={<ProtectedRoute><Manifestes /></ProtectedRoute>} />
            <Route path="/reseau/tournees" element={<ProtectedRoute><Tournees /></ProtectedRoute>} />
            <Route path="/reseau/compensation" element={<ProtectedRoute><Compensation /></ProtectedRoute>} />
            <Route path="/transit" element={<ProtectedRoute><DossiersBoard /></ProtectedRoute>} />
            <Route path="/transit/:id" element={<ProtectedRoute><DossierDetail /></ProtectedRoute>} />
            <Route path="/create-colis" element={<ProtectedRoute><CreateColis /></ProtectedRoute>} />
            <Route path="/colis/:id" element={<ProtectedRoute><ColisDetails /></ProtectedRoute>} />
            <Route path="/livreurs" element={<ProtectedRoute><LivreursList /></ProtectedRoute>} />
            <Route path="/livreurs/:id" element={<ProtectedRoute><LivreurDetails /></ProtectedRoute>} />
            <Route path="/factures" element={<ProtectedRoute><FacturesList /></ProtectedRoute>} />
            <Route path="/factures/create" element={<ProtectedRoute><CreateFacture /></ProtectedRoute>} />
            <Route path="/factures/:id" element={<ProtectedRoute><FactureDetails /></ProtectedRoute>} />
            <Route path="/factures/:id/edit" element={<ProtectedRoute><EditFacture /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
            <Route path="/notifications/templates" element={<ProtectedRoute><NotificationTemplates /></ProtectedRoute>} />
            <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />

            {/* Profile — accessible without tenant */}
            <Route path="/profile" element={<ProtectedRoute requireTenant={false}><UserProfile /></ProtectedRoute>} />

            {/* Livreur COD Wallet */}
            <Route path="/livreur-wallet/:livreur_id" element={<ProtectedRoute><LivreurWallet /></ProtectedRoute>} />

            {/* Super admin only */}
            <Route path="/super-admin/config" element={<SuperAdminRoute><SuperAdminConfig /></SuperAdminRoute>} />
            <Route path="/super-admin/quota" element={<SuperAdminRoute><QuotaManagement /></SuperAdminRoute>} />
            <Route path="/super-admin/system" element={<SuperAdminRoute><SystemMonitor /></SuperAdminRoute>} />
            <Route path="/QA" element={<SuperAdminRoute><QARegression /></SuperAdminRoute>} />

            <Route path="/" element={<Navigate to="/login" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
